import React, { useState, useEffect, useRef } from 'react';
import { useHistory } from 'react-router-dom';
import './ContactForm.css';
// import imgStage from '../../assets/Product and Services/who_we_are_stage.webp';

// Direct submission to avoid CORS preflight
const GOOGLE_SCRIPT_URL = 'https://script.google.com/macros/s/AKfycbziFug49j3QtH8sgVoE_a-fOtjAnyBkTYgOqTiAoBbpgLC1lZxS0EdQwuu5OZewGetGiw/exec';

const SubmitToGoogleSheets = async (submissionData) => {

    try {
        // Use FormData to avoid CORS preflight OPTIONS request
        const formData = new FormData();
        formData.append('data', JSON.stringify(submissionData));

        const response = await fetch(GOOGLE_SCRIPT_URL, {
            method: 'POST',
            body: formData
        });

        // Since we can't read response with no-cors, assume success if no error thrown
        if (!response.ok && response.status !== 0) {
            throw new Error(`HTTP error! status: ${response.status}`);
        }

        return { success: true, message: 'Data submitted successfully' };

    } catch (error) {
        console.error('Error submitting to Google Sheets:', error);
        throw new Error(`Failed to submit form: ${error.message}`);
    }
};



// Cookie consent helper functions
function getCookie(name) {
    return document.cookie.split('; ').reduce((r, v) => {
        const parts = v.split('=');
        return parts[0] === name ? decodeURIComponent(parts[1]) : r;
    }, '');
}

const ContactForm = () => {
    const history = useHistory();
    const [formData, setFormData] = useState({
        reasonFor: '',
        yourMessage: '',
        salutation: 'Mr.',
        firstName: '',
        lastName: '',
        email: '',
        phoneNumber: '',
        privacyAgreement: false
    });

    const [errors, setErrors] = useState({});
    const [charCount, setCharCount] = useState(0);
    const [selectedFiles, setSelectedFiles] = useState([]);
    const [cookieConsent, setCookieConsent] = useState({
        analytics: false,
        marketing: false,
        consentStatus: 'none'
    });
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submitError, setSubmitError] = useState('');
    const fileInputRef = useRef(null);

    useEffect(() => {
        window.scrollTo(0, 0);

        // Check cookie consent status
        const consent = getCookie('HFCookieConsent');
        const measurementConsent = getCookie('HFMeasurementConsent') === 'true';
        const marketingConsent = getCookie('HFMarketingConsent') === 'true';

        setCookieConsent({
            analytics: consent === 'accepted' || (consent === 'custom' && measurementConsent),
            marketing: consent === 'accepted' || (consent === 'custom' && marketingConsent),
            consentStatus: consent || 'none'
        });

        // Listen for cookie consent changes
        const handleConsentChange = (event) => {
            const { measurement, marketing, consent } = event.detail;
            setCookieConsent(prev => ({
                ...prev,
                analytics: measurement,
                marketing: marketing,
                consentStatus: consent
            }));
        };

        window.addEventListener('cookieConsentChanged', handleConsentChange);
        return () => window.removeEventListener('cookieConsentChanged', handleConsentChange);
    }, []);

    // Track form interactions if analytics consent is given
    const trackFormInteraction = (action, data = {}) => {
        if (cookieConsent.analytics && window.gtag) {
            window.gtag('event', action, {
                event_category: 'contact_form',
                event_label: 'contact_form_interaction',
                ...data
            });
        }
    };

    const handleInputChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));

        // Clear error when user starts typing
        if (errors[name]) {
            setErrors(prev => ({
                ...prev,
                [name]: ''
            }));
        }

        // Handle character count for message
        if (name === 'yourMessage') {
            setCharCount(value.length);
        }

        // Track form interactions if analytics consent is given
        if (name !== 'privacyAgreement') {
            trackFormInteraction('form_field_interaction', {
                field_name: name,
                field_type: type
            });
        }
    };

    const handleFileChange = (e) => {
        const files = Array.from(e.target.files);
        const validFiles = files.filter(file => {
            const maxSize = 12 * 1024 * 1024; // 12MB
            if (file.size > maxSize) {
                alert(`File ${file.name} is too large. Maximum size is 12MB.`);
                return false;
            }
            return true;
        });
        setSelectedFiles(validFiles);
    };

    const handleFileUploadClick = () => {
        fileInputRef.current.click();
    };

    const removeFile = (index) => {
        setSelectedFiles(prev => prev.filter((_, i) => i !== index));
    };

    const validateForm = () => {
        const newErrors = {};

        // Basic validation
        if (!formData.reasonFor) {
            newErrors.reasonFor = 'This field is required.';
        }

        if (!formData.yourMessage) {
            newErrors.yourMessage = 'This field is required.';
        }

        if (!formData.firstName) {
            newErrors.firstName = 'This field is required.';
        }

        if (!formData.lastName) {
            newErrors.lastName = 'This field is required.';
        }

        if (!formData.email) {
            newErrors.email = 'This field is required.';
        } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
            newErrors.email = 'Please enter a valid email address.';
        }

        if (!formData.privacyAgreement) {
            newErrors.privacyAgreement = 'You must agree to the privacy statement and marketing communications.';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setSubmitError('');

        if (validateForm()) {
            setIsSubmitting(true);

            try {
                // Track form submission if analytics consent is given
                trackFormInteraction('form_submission', {
                    reason_for_contact: formData.reasonFor,
                    has_files: selectedFiles.length > 0,
                    consent_status: cookieConsent.consentStatus,
                    marketing_consent: formData.privacyAgreement,
                    cookie_marketing_consent: cookieConsent.marketing
                });

                // Prepare data for Google Sheets
                const submissionData = {
                    salutation: formData.salutation,
                    firstName: formData.firstName,
                    lastName: formData.lastName,
                    email: formData.email,
                    phoneNumber: formData.phoneNumber,
                    reasonFor: formData.reasonFor,
                    yourMessage: formData.yourMessage,
                    filesAttached: selectedFiles.length > 0 ? 'Yes' : 'No',
                    fileDetails: selectedFiles.map(file => ({
                        name: file.name,
                        type: file.type,
                        size: file.size,
                        sizeMB: (file.size / 1024 / 1024).toFixed(2)
                    })),
                    marketingConsent: formData.privacyAgreement ? 'Yes' : 'No',
                    subscription: formData.privacyAgreement ? 'Yes' : 'No',
                    cookieAnalyticsConsent: cookieConsent.analytics ? 'Yes' : 'No',
                    cookieMarketingConsent: cookieConsent.marketing ? 'Yes' : 'No'
                };

                // Send data to Google Sheets via Apps Script
                await SubmitToGoogleSheets(submissionData);

                // Set form submission cookie to prevent cookie banner from showing again
                const expires = new Date(Date.now() + 365 * 864e5).toUTCString();
                document.cookie = `HFFormSubmitted=true; expires=${expires}; path=/; SameSite=Lax`;

                // If user agreed to privacy statement, set marketing consent cookie
                if (formData.privacyAgreement) {
                    document.cookie = `HFMarketingConsent=true; expires=${expires}; path=/; SameSite=Lax`;
                    window.HFMarketingEnabled = true;
                }

                // Redirect to thank you page
                history.push('/thank-you');

            } catch (error) {
                console.error('Form submission error:', error);

                // Provide more specific error messages based on error type
                let errorMessage = 'Failed to submit form. Please try again or contact us directly.';

                if (error.message.includes('timeout') || error.message.includes('abort')) {
                    errorMessage = 'Request timed out. Please check your internet connection and try again.';
                } else if (error.message.includes('CORS') || error.message.includes('NetworkError')) {
                    errorMessage = 'Network error. Please check your internet connection and try again.';
                } else if (error.message.includes('HTTP error! status: 404')) {
                    errorMessage = 'Service temporarily unavailable. Please try again later or contact us directly.';
                } else if (error.message.includes('HTTP error! status: 500')) {
                    errorMessage = 'Server error. Please try again later or contact us directly.';
                } else if (error.message) {
                    errorMessage = error.message;
                }

                setSubmitError(errorMessage);
            } finally {
                setIsSubmitting(false);
            }
        }
    };



    // Generate personalized privacy message based on consent
    const getPrivacyMessage = () => {
        if (cookieConsent.consentStatus === 'none') {
            return "Please set your cookie preferences to understand how your data will be processed. Note: Submitting this form will automatically set your consent preferences and includes agreement to receive marketing communications.";
        } else if (cookieConsent.analytics && cookieConsent.marketing) {
            return "Your form data will be processed for customer service purposes. Analytics and marketing cookies are enabled, so we may also use this data to improve our services and send you relevant updates.";
        } else if (cookieConsent.analytics) {
            return "Your form data will be processed for customer service purposes. Analytics cookies are enabled, so we may also use this data to improve our services.";
        } else if (cookieConsent.marketing) {
            return "Your form data will be processed for customer service purposes. Marketing cookies are enabled, so we may also send you relevant updates about our products and services.";
        } else {
            return "Your form data will be processed for customer service purposes only. Analytics and marketing tracking are disabled as per your preferences.";
        }
    };

    return (
        <div>
            {/* <CenterStage imageSrc={imgStage} title="Contact Form" paragraph={"Get in touch with us for any inquiries or support"} /> */}
            <div className="contact-form-container">
                <div className="contact-form-wrapper">
                    {/* <div className="contact-form-header">
                    <h1>Contact Us</h1>
                    <p>Get in touch with us for any inquiries or support</p>
                </div> */}

                    <form onSubmit={handleSubmit} className="contact-form">
                        {/* Your Request Section */}
                        <div className="form-section">
                            <div className="form-header-group">
                                <h3 className="form-header">Enquiry Form</h3>
                                <div className="form-subHeader">How can we help you?</div>
                            </div>

                            <div className="form-line">
                                <label className="form-label">
                                    Reason for Contact <span className="form-required">*</span>
                                </label>
                                <div className="form-input">
                                    <select
                                        className={`form-dropdown ${errors.reasonFor ? 'form-validation-error' : ''}`}
                                        name="reasonFor"
                                        value={formData.reasonFor}
                                        onChange={handleInputChange}
                                        required
                                    >
                                        <option value="">Select a reason</option>
                                        <option value="Enquiry Price and Availability">Enquiry Price and Availability</option>
                                        <option value="General Enquiry">General Enquiry</option>
                                        <option value="Technical Consultation">Technical Consultation</option>
                                        <option value="3D CAD Model">3D CAD Model</option>
                                        <option value="E-Learning">E-Learning</option>
                                        <option value="Request to Marketing Department">Request to Marketing Department</option>
                                        <option value="Request to Purchasing Department">Request to Purchasing Department</option>
                                        <option value="Request to HR Department">Request to HR Department</option>
                                    </select>
                                    {errors.reasonFor && (
                                        <div className="form-error-message">
                                            <img src="https://cdn.jotfor.ms/images/exclamation-octagon.png" alt="title" />
                                            <span>{errors.reasonFor}</span>
                                        </div>
                                    )}
                                </div>
                            </div>

                            <div className="form-line">
                                <label className="form-label">
                                    Your Message <span className="form-required">*</span>
                                </label>
                                <div className="form-input">
                                    <div className="form-textarea-limit">
                                        <textarea
                                            className={`form-textarea ${errors.yourMessage ? 'form-validation-error' : ''}`}
                                            name="yourMessage"
                                            value={formData.yourMessage}
                                            onChange={handleInputChange}
                                            rows="4"
                                            maxLength="150"
                                            required
                                        />
                                        <div className="form-textarea-limit-indicator">
                                            {charCount}/150
                                        </div>
                                    </div>
                                    {errors.yourMessage && (
                                        <div className="form-error-message">
                                            <img src="https://cdn.jotfor.ms/images/exclamation-octagon.png" alt="title" />
                                            <span>{errors.yourMessage}</span>
                                        </div>
                                    )}
                                </div>
                            </div>

                            {/* <div className="form-line">
                                <label className="form-label">Add an attachment</label>
                                <div className="form-input">
                                    <div className="file-upload-container">
                                        <input
                                            ref={fileInputRef}
                                            type="file"
                                            className="file-upload-input"
                                            multiple
                                            accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                                            onChange={handleFileChange}
                                        />
                                        <button
                                            type="button"
                                            className="file-upload-button"
                                            onClick={handleFileUploadClick}
                                        >
                                            Browse Files
                                        </button>
                                        <div className="file-upload-info">Max. file size: 12 MB</div>

                                        {selectedFiles.length > 0 && (
                                            <div className="selected-files">
                                                <h4>Selected Files:</h4>
                                                <ul className="file-list">
                                                    {selectedFiles.map((file, index) => (
                                                        <li key={index} className="file-item">
                                                            <span className="file-name">{file.name}</span>
                                                            <span className="file-size">({(file.size / 1024 / 1024).toFixed(2)} MB)</span>
                                                            <button
                                                                type="button"
                                                                className="remove-file-btn"
                                                                onClick={() => removeFile(index)}
                                                            >
                                                                ×
                                                            </button>
                                                        </li>
                                                    ))}
                                                </ul>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div> */}
                        </div>

                        {/* Your Contact Information Section */}
                        <div className="form-section">
                            <div className="form-header-group">
                                <h3 className="form-header">Your Contact Information</h3>
                                <div className="form-subHeader">How can we get in touch?</div>
                            </div>

                            <div className="form-line">
                                <label className="form-label">
                                    Name <span className="form-required">*</span>
                                </label>
                                <div className="form-input name-inputs">
                                    <div className="name-input-group">
                                        <select
                                            className="form-dropdown"
                                            name="salutation"
                                            value={formData.salutation}
                                            onChange={handleInputChange}
                                        >
                                            <option value="Mr.">Mr.</option>
                                            <option value="Mrs.">Mrs.</option>
                                            <option value="other">other</option>
                                        </select>
                                        <label className="form-sub-label">Salutation</label>
                                    </div>
                                    <div className="name-input-group">
                                        <input
                                            type="text"
                                            className={`form-textbox ${errors.firstName ? 'form-validation-error' : ''}`}
                                            name="firstName"
                                            value={formData.firstName}
                                            onChange={handleInputChange}
                                            required
                                        />
                                        <label className="form-sub-label">First Name</label>
                                        {errors.firstName && (
                                            <div className="form-error-message">
                                                <span>{errors.firstName}</span>
                                            </div>
                                        )}
                                    </div>
                                    <div className="name-input-group">
                                        <input
                                            type="text"
                                            className={`form-textbox ${errors.lastName ? 'form-validation-error' : ''}`}
                                            name="lastName"
                                            value={formData.lastName}
                                            onChange={handleInputChange}
                                            required
                                        />
                                        <label className="form-sub-label">Last Name</label>
                                        {errors.lastName && (
                                            <div className="form-error-message">
                                                <span>{errors.lastName}</span>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>

                            <div className="form-line-row">
                                <div className="form-line half">
                                    <label className="form-label">
                                        E-Mail <span className="form-required">*</span>
                                    </label>
                                    <div className="form-input">
                                        <input
                                            type="email"
                                            className={`form-textbox ${errors.email ? 'form-validation-error' : ''}`}
                                            name="email"
                                            value={formData.email}
                                            onChange={handleInputChange}
                                            required
                                        />
                                        {errors.email && (
                                            <div className="form-error-message">
                                                <span>{errors.email}</span>
                                            </div>
                                        )}
                                    </div>
                                </div>

                                <div className="form-line half">
                                    <label className="form-label">Phone Number</label>
                                    <div className="form-input">
                                        <input
                                            type="text"
                                            className="form-textbox"
                                            name="phoneNumber"
                                            value={formData.phoneNumber}
                                            onChange={handleInputChange}
                                        />
                                    </div>
                                </div>
                            </div>

                            <div className="form-line">
                                <div className="form-input">
                                    <div className="privacy-agreement">
                                        <input
                                            type="checkbox"
                                            className="form-checkbox"
                                            name="privacyAgreement"
                                            checked={formData.privacyAgreement}
                                            onChange={handleInputChange}
                                            required
                                        />
                                        <label className="privacy-text">
                                            Yes, I agree to the processing of my data in accordance with the{' '}
                                            <a href="#" target="_blank" rel="noopener noreferrer">
                                                Data Privacy Statement
                                            </a>{' '}
                                            and I agree that my data may be passed on to authorised HANSA-FLEX Ireland or trade partners in order to be able to offer the best possible service and rapid processing of the enquiry. I also consent to receive marketing communications about our products, services, and industry updates. I understand I can unsubscribe at any time using the link in our emails. <span className="form-required">*</span>
                                        </label>
                                        {errors.privacyAgreement && (
                                            <div className="form-error-message">
                                                <span>{errors.privacyAgreement}</span>
                                            </div>
                                        )}
                                    </div>
                                </div>
                                {/* Personalized privacy notice */}
                                <div className="privacy-notice" style={{
                                    marginTop: '10px',
                                    padding: '10px',
                                    backgroundColor: '#f8f9fa',
                                    borderRadius: '4px',
                                    fontSize: '12px',
                                    color: '#666'
                                }}>
                                    <strong>Privacy Notice:</strong> {getPrivacyMessage()}
                                    {cookieConsent.consentStatus === 'none' && (
                                        <div style={{ marginTop: '5px' }}>
                                            <a href="#" onClick={(e) => {
                                                e.preventDefault();
                                                // Trigger cookie banner to show
                                                window.dispatchEvent(new CustomEvent('showCookieBanner'));
                                            }} style={{ color: '#2c5aa0', textDecoration: 'underline' }}>
                                                Manage Cookie Preferences
                                            </a>
                                            <br />
                                            <small style={{ color: '#888', fontStyle: 'italic' }}>
                                                Note: Submitting this form will automatically set your consent preferences and the cookie banner won't appear again.
                                            </small>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>

                        <div className="form-submit-section">
                            <button type="submit" className="form-submit-button" disabled={isSubmitting}>
                                {isSubmitting ? 'Sending...' : 'Send Message'}
                            </button>
                            {submitError && (
                                <div className="form-error-message" style={{ marginTop: '10px' }}>
                                    <img src="https://cdn.jotfor.ms/images/exclamation-octagon.png" alt="title" />
                                    <span>{submitError}</span>
                                </div>
                            )}
                        </div>
                    </form>
                </div>
            </div>

        </div>
    );
};

export default ContactForm; 